import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  useColorScheme,
  Dimensions,
  StyleSheet,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ProgressChart } from 'react-native-chart-kit';
import WebECharts from '../components/WebECharts';
import ECharts from '../components/ECharts';

const screenWidth = Dimensions.get('window').width;
// 根据平台选择合适的图表组件
const ChartComponent = Platform.OS === 'web' ? WebECharts : ECharts;

const HealthDataOverviewScreen = ({ navigation }) => {
  const isDarkMode = useColorScheme() === 'dark';
  const [activeTab, setActiveTab] = useState('basicMetrics');
  
  // 模拟数据 - 在实际项目中应从API或本地存储获取
  const [userData, setUserData] = useState({
    gender: '男',  // 性别
    age: 30,      // 年龄
    height: 175,   // 身高(cm)
    weight: 70,    // 体重(kg)
    targetWeight: 65,  // 目标体重(kg)
  });
  
  // 体重历史数据
  const [weightHistory, setWeightHistory] = useState([]);
  
  // BMI历史数据
  const [bmiHistory, setBmiHistory] = useState([]);
  
  // 每日营养摄入数据
  const [nutritionIntake, setNutritionIntake] = useState({
    today: {
      calories: 1800,
      protein: 85,
      carbs: 220,
      fat: 60,
    },
    recommended: {
      calories: 2000,
      protein: 100,
      carbs: 250,
      fat: 70,
    },
    weekHistory: []
  });
  
  // 生成最近7天的数据
  useEffect(() => {
    // 生成过去7天的体重数据
    const generateLast7DaysWeightData = () => {
      // 起始体重：当前体重+0.5kg
      let startWeight = userData.weight + 0.5;
      // 每天平均减轻体重(kg)
      const dailyWeightLoss = 0.5 / 7;
      
      const weightData = [];
      for (let i = 6; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const dateStr = `${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
        
        // 添加一些随机变化，但保持整体下降趋势
        const randomVariation = (Math.random() * 0.2 - 0.1); // -0.1kg ~ 0.1kg
        const weightValue = startWeight - (6 - i) * dailyWeightLoss + randomVariation;
        
        weightData.push({
          date: dateStr,
          value: parseFloat(weightValue.toFixed(1))
        });
      }
      
      setWeightHistory(weightData);
    };
    
    // 生成过去7天的营养摄入数据
    const generateLast7DaysData = () => {
      const last7Days = [];
      for (let i = 6; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const dateStr = `${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
        
        // 添加一些随机变化，但保持数据在合理范围内
        const variance = Math.random() * 0.1 + 0.95; // 在95%~105%之间波动
        const dayOfWeek = date.getDay(); // 0是周日，6是周六
        const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
        const weekendFactor = isWeekend ? 1.05 : 1; // 周末摄入量略高
        
        last7Days.push({
          date: dateStr,
          calories: Math.round(nutritionIntake.recommended.calories * variance * weekendFactor),
          protein: Math.round(nutritionIntake.recommended.protein * variance * weekendFactor),
          carbs: Math.round(nutritionIntake.recommended.carbs * variance * weekendFactor),
          fat: Math.round(nutritionIntake.recommended.fat * variance * weekendFactor)
        });
      }
      
      setNutritionIntake(prev => ({
        ...prev,
        weekHistory: last7Days
      }));
    };
    
    generateLast7DaysWeightData();
    generateLast7DaysData();
  }, []);
  
  // 计算BMI历史数据
  useEffect(() => {
    const bmiData = weightHistory.map(item => {
      const bmi = item.value / ((userData.height / 100) * (userData.height / 100));
      return { date: item.date, value: parseFloat(bmi.toFixed(1)) };
    });
    setBmiHistory(bmiData);
  }, [weightHistory, userData.height]);
  
  // 计算BMI状态
  const getBMIStatus = (bmi) => {
    if (bmi < 18.5) return { status: "偏瘦", color: "#60a5fa" };
    if (bmi < 24) return { status: "正常", color: "#34d399" };
    if (bmi < 28) return { status: "超重", color: "#fbbf24" };
    return { status: "肥胖", color: "#ef4444" };
  };
  
  // 计算基础代谢率(BMR)
  const calculateBMR = () => {
    // 使用Mifflin-St Jeor公式
    let bmr;
    if (userData.gender === '男') {
      bmr = 10 * userData.weight + 6.25 * userData.height - 5 * userData.age + 5;
    } else {
      bmr = 10 * userData.weight + 6.25 * userData.height - 5 * userData.age - 161;
    }
    return Math.round(bmr);
  };
  
  // 计算营养摄入达成度
  const calculateNutrientCompletion = () => {
    return {
      calories: nutritionIntake.today.calories / nutritionIntake.recommended.calories,
      protein: nutritionIntake.today.protein / nutritionIntake.recommended.protein,
      carbs: nutritionIntake.today.carbs / nutritionIntake.recommended.carbs,
      fat: nutritionIntake.today.fat / nutritionIntake.recommended.fat,
    };
  };
  
  // 计算总体目标达成率
  const calculateOverallCompletion = () => {
    const completion = calculateNutrientCompletion();
    // 总体达成率为各营养素达成率的平均值，但最高不超过100%
    const avg = (completion.calories + completion.protein + completion.carbs + completion.fat) / 4;
    return Math.min(avg, 1);
  };
  
  // 图表配置
  const chartConfig = {
    backgroundGradientFrom: isDarkMode ? '#a9b6d0' : '#ffffff',
    backgroundGradientTo: isDarkMode ? '#a9b6d0' : '#ffffff',
    color: (opacity = 1) => isDarkMode ? `rgba(255, 255, 255, ${opacity})` : `rgba(0, 0, 0, ${opacity})`,
    strokeWidth: 2,
    decimalPlaces: 1,
    style: {
      borderRadius: 16,
    },
    propsForDots: {
      r: "4",
      strokeWidth: "2",
      stroke: "#2563eb"
    }
  };
  
  // 进度图表配置
  const progressChartConfig = {
    ...chartConfig,
    color: (opacity = 1) => `rgba(37, 99, 235, ${opacity})`,
    strokeWidth: 2,
    backgroundGradientFromOpacity: 0,
    backgroundGradientToOpacity: 0,
  };
  
  // 准备营养摄入达成度数据
  const getNutrientCompletionData = () => {
    const completion = calculateNutrientCompletion();
    // 进度图需要数组形式的数据
    return {
      labels: ["卡路里", "蛋白质", "碳水", "脂肪"],
      data: [
        Math.min(completion.calories, 1),
        Math.min(completion.protein, 1),
        Math.min(completion.carbs, 1),
        Math.min(completion.fat, 1)
      ]
    };
  };
  
  // BMI的当前值
  const currentBMI = bmiHistory.length > 0 ? bmiHistory[bmiHistory.length - 1].value : 0;
  const bmiStatus = getBMIStatus(currentBMI);
  
  // BMR值
  const bmr = calculateBMR();
  
  // 总体达成率
  const overallCompletion = calculateOverallCompletion();
  
  // 准备BMI图表数据 - ECharts格式
  const getBMIChartDataECharts = () => {
    return {
      tooltip: {
        trigger: 'axis',
        formatter: function(params) {
          let tooltip = params[0].axisValue + '<br/>';
          params.forEach(param => {
            tooltip += param.marker + ' ' + param.seriesName + ': ' + param.value + '<br/>';
          });
          return tooltip;
        }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        top: '8%',
        containLabel: true
      },
      legend: {
        data: ['BMI', '偏瘦(18.5)', '正常(24)', '超重(28)'],
        textStyle: {
          color: isDarkMode ? '#e0e0e0' : '#666'
        }
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: bmiHistory.map(item => item.date),
        axisLine: {
          lineStyle: {
            color: isDarkMode ? '#a9b6d0' : '#666'
          }
        },
        axisLabel: {
          color: isDarkMode ? '#e0e0e0' : '#666',
          fontSize: 10
        }
      },
      yAxis: {
        type: 'value',
        axisLine: {
          lineStyle: {
            color: isDarkMode ? '#a9b6d0' : '#666'
          }
        },
        axisLabel: {
          color: isDarkMode ? '#e0e0e0' : '#666',
          fontSize: 10
        },
        splitLine: {
          lineStyle: {
            color: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
          }
        }
      },
      series: [
        {
          name: 'BMI',
          data: bmiHistory.map(item => item.value),
          type: 'line',
          smooth: true,
          symbolSize: 6,
          itemStyle: {
            color: '#2563eb'
          },
          lineStyle: {
            width: 3,
            color: '#2563eb'
          },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [{
                offset: 0,
                color: '#2563ebCC' // 带透明度的颜色
              }, {
                offset: 1,
                color: '#2563eb11'
              }]
            }
          }
        },
        {
          name: '偏瘦(18.5)',
          data: bmiHistory.map(() => 18.5),
          type: 'line',
          symbol: 'none',
          lineStyle: {
            width: 1,
            color: '#60a5fa',
            type: 'dashed'
          }
        },
        {
          name: '正常(24)',
          data: bmiHistory.map(() => 24),
          type: 'line',
          symbol: 'none',
          lineStyle: {
            width: 1,
            color: '#34d399',
            type: 'dashed'
          }
        },
        {
          name: '超重(28)',
          data: bmiHistory.map(() => 28),
          type: 'line',
          symbol: 'none',
          lineStyle: {
            width: 1,
            color: '#fbbf24',
            type: 'dashed'
          }
        }
      ]
    };
  };
  
  // 准备体重图表数据 - ECharts格式
  const getWeightChartDataECharts = () => {
    return {
      tooltip: {
        trigger: 'axis',
        formatter: function(params) {
          let tooltip = params[0].axisValue + '<br/>';
          params.forEach(param => {
            tooltip += param.marker + ' ' + param.seriesName + ': ' + param.value + ' kg<br/>';
          });
          return tooltip;
        }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        top: '8%',
        containLabel: true
      },
      legend: {
        data: ['当前体重', '目标体重'],
        textStyle: {
          color: isDarkMode ? '#e0e0e0' : '#666'
        }
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: weightHistory.map(item => item.date),
        axisLine: {
          lineStyle: {
            color: isDarkMode ? '#a9b6d0' : '#666'
          }
        },
        axisLabel: {
          color: isDarkMode ? '#e0e0e0' : '#666',
          fontSize: 10
        }
      },
      yAxis: {
        type: 'value',
        axisLine: {
          lineStyle: {
            color: isDarkMode ? '#a9b6d0' : '#666'
          }
        },
        axisLabel: {
          color: isDarkMode ? '#e0e0e0' : '#666',
          fontSize: 10
        },
        splitLine: {
          lineStyle: {
            color: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
          }
        }
      },
      series: [
        {
          name: '当前体重',
          data: weightHistory.map(item => item.value),
          type: 'line',
          smooth: true,
          symbolSize: 6,
          itemStyle: {
            color: '#2563eb'
          },
          lineStyle: {
            width: 3,
            color: '#2563eb'
          },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [{
                offset: 0,
                color: '#2563ebCC' // 带透明度的颜色
              }, {
                offset: 1,
                color: '#2563eb11'
              }]
            }
          }
        },
        {
          name: '目标体重',
          data: weightHistory.map(() => userData.targetWeight),
          type: 'line',
          symbol: 'none',
          lineStyle: {
            width: 2,
            color: '#dc2626',
            type: 'dashed'
          }
        }
      ]
    };
  };
  
  // 准备营养摄入趋势图表数据 - ECharts格式
  const getNutritionHistoryDataECharts = () => {
    return {
      tooltip: {
        trigger: 'axis',
        formatter: function(params) {
          return params[0].axisValue + '<br/>' + 
                 params[0].marker + ' 卡路里达成率: ' + 
                 Math.round(params[0].value * 100) + '%';
        }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        top: '8%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: nutritionIntake.weekHistory.map(item => item.date.split('-')[1] + '日'),
        axisLine: {
          lineStyle: {
            color: isDarkMode ? '#a9b6d0' : '#666'
          }
        },
        axisLabel: {
          color: isDarkMode ? '#e0e0e0' : '#666',
          fontSize: 10
        }
      },
      yAxis: {
        type: 'value',
        axisLine: {
          lineStyle: {
            color: isDarkMode ? '#a9b6d0' : '#666'
          }
        },
        axisLabel: {
          color: isDarkMode ? '#e0e0e0' : '#666',
          fontSize: 10,
          formatter: '{value}%'
        },
        max: 100,
        splitLine: {
          lineStyle: {
            color: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
          }
        }
      },
      series: [{
        name: '卡路里达成率',
        data: nutritionIntake.weekHistory.map(item => Math.round(item.calories / nutritionIntake.recommended.calories * 100)),
        type: 'line',
        smooth: true,
        symbolSize: 6,
        itemStyle: {
          color: '#2563eb'
        },
        lineStyle: {
          width: 3,
          color: '#2563eb'
        },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [{
              offset: 0,
              color: '#2563ebCC' // 带透明度的颜色
            }, {
              offset: 1,
              color: '#2563eb11'
            }]
          }
        }
      }]
    };
  };
  
  return (
    <ScrollView 
      style={{
        flex: 1,
        backgroundColor: isDarkMode ? '#8fa0c1' : '#f9fafb',
      }}
      contentContainerStyle={{ padding: 16 }}
    >
      {/* 页面导航标签 */}
      <View style={{
        flexDirection: 'row',
        marginBottom: 16,
        backgroundColor: isDarkMode ? '#a9b6d0' : '#ffffff',
        borderRadius: 8,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.1,
        shadowRadius: 2,
        elevation: 2,
      }}>
        <TouchableOpacity
          style={{
            flex: 1,
            paddingVertical: 12,
            alignItems: 'center',
            borderBottomWidth: activeTab === 'basicMetrics' ? 2 : 0,
            borderBottomColor: '#2563eb',
          }}
          onPress={() => setActiveTab('basicMetrics')}
        >
          <Text style={{
            fontWeight: activeTab === 'basicMetrics' ? 'bold' : 'normal',
            color: activeTab === 'basicMetrics' ? '#2563eb' : (isDarkMode ? '#e0e0e0' : '#6b7280'),
          }}>
            基础指标
          </Text>
        </TouchableOpacity>
        
        <TouchableOpacity
          style={{
            flex: 1,
            paddingVertical: 12,
            alignItems: 'center',
            borderBottomWidth: activeTab === 'nutritionIntake' ? 2 : 0,
            borderBottomColor: '#2563eb',
          }}
          onPress={() => setActiveTab('nutritionIntake')}
        >
          <Text style={{
            fontWeight: activeTab === 'nutritionIntake' ? 'bold' : 'normal',
            color: activeTab === 'nutritionIntake' ? '#2563eb' : (isDarkMode ? '#e0e0e0' : '#6b7280'),
          }}>
            营养摄入
          </Text>
        </TouchableOpacity>
      </View>
      
      {activeTab === 'basicMetrics' ? (
        <>
          {/* 个人基础指标展示区 */}
          <View style={{
            borderRadius: 12,
            padding: 16,
            marginBottom: 16,
            backgroundColor: isDarkMode ? '#a9b6d0' : '#ffffff',
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.1,
            shadowRadius: 4,
            elevation: 2,
          }}>
            <Text style={{
              fontSize: 18,
              fontWeight: 'bold',
              marginBottom: 16,
              color: isDarkMode ? 'white' : '#1f2937',
            }}>
              个人基础指标
            </Text>
            
            {/* BMI指数 */}
            <View style={{
              alignItems: 'center',
              padding: 16,
              backgroundColor: isDarkMode ? '#8295b7' : '#f3f4f6',
              borderRadius: 8,
              marginBottom: 16,
            }}>
              <Text style={{
                fontSize: 16,
                color: isDarkMode ? '#e0e0e0' : '#4b5563',
                marginBottom: 8,
              }}>
                BMI指数
              </Text>
              <Text style={{
                fontSize: 28,
                fontWeight: 'bold',
                color: bmiStatus.color,
                marginBottom: 4,
              }}>
                {currentBMI}
              </Text>
              <Text style={{
                fontSize: 14,
                color: bmiStatus.color,
              }}>
                {bmiStatus.status}
              </Text>
            </View>
            
            {/* BMI趋势图 */}
            <Text style={{
              fontSize: 16,
              fontWeight: 'bold',
              marginBottom: 8,
              color: isDarkMode ? '#e0e0e0' : '#4b5563',
            }}>
              BMI指数趋势图与健康区间对比
            </Text>
            <View style={{
              marginBottom: 20,
              alignItems: 'center',
              backgroundColor: isDarkMode ? '#8295b7' : '#f3f4f6',
              borderRadius: 8,
              padding: 8,
            }}>
              <ChartComponent
                option={getBMIChartDataECharts()}
                height={220}
                width={screenWidth - 64}
              />
            </View>
            
            {/* 体重变化曲线 */}
            <Text style={{
              fontSize: 16,
              fontWeight: 'bold',
              marginBottom: 8,
              color: isDarkMode ? '#e0e0e0' : '#4b5563',
            }}>
              体重变化曲线与目标体重对比
            </Text>
            <View style={{
              marginBottom: 20,
              alignItems: 'center',
              backgroundColor: isDarkMode ? '#8295b7' : '#f3f4f6',
              borderRadius: 8,
              padding: 8,
            }}>
              <ChartComponent
                option={getWeightChartDataECharts()}
                height={220}
                width={screenWidth - 64}
              />
            </View>
            
            {/* 近7天卡路里摄入趋势 */}
            <Text style={{
              fontSize: 16,
              fontWeight: 'bold',
              marginBottom: 8,
              color: isDarkMode ? '#e0e0e0' : '#4b5563',
            }}>
              近7天卡路里摄入达成率
            </Text>
            <View style={{
              marginBottom: 20,
              alignItems: 'center',
              backgroundColor: isDarkMode ? '#8295b7' : '#f3f4f6',
              borderRadius: 8,
              padding: 8,
            }}>
              <ChartComponent
                option={getNutritionHistoryDataECharts()}
                height={200}
                width={screenWidth - 64}
              />
            </View>
            
            {/* 基础代谢率 */}
            <Text style={{
              fontSize: 16,
              fontWeight: 'bold',
              marginBottom: 8,
              color: isDarkMode ? '#e0e0e0' : '#4b5563',
            }}>
              基础代谢率(BMR)计算结果
            </Text>
            <View style={{
              padding: 16,
              backgroundColor: isDarkMode ? '#8295b7' : '#f3f4f6',
              borderRadius: 8,
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <View>
                <Text style={{
                  fontSize: 14,
                  color: isDarkMode ? '#e0e0e0' : '#6b7280',
                  marginBottom: 4,
                }}>
                  您的基础代谢率
                </Text>
                <Text style={{
                  fontSize: 16,
                  color: isDarkMode ? 'white' : '#1f2937',
                }}>
                  每日消耗大约<Text style={{ fontWeight: 'bold', color: '#2563eb' }}>{bmr}</Text>卡路里
                </Text>
              </View>
              <Ionicons 
                name="flame-outline" 
                size={36} 
                color="#2563eb" 
              />
            </View>
          </View>
        </>
      ) : (
        <>
          {/* 营养摄入达成度 */}
          <View style={{
            borderRadius: 12,
            padding: 16,
            marginBottom: 16,
            backgroundColor: isDarkMode ? '#a9b6d0' : '#ffffff',
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.1,
            shadowRadius: 4,
            elevation: 2,
          }}>
            <Text style={{
              fontSize: 18,
              fontWeight: 'bold',
              marginBottom: 16,
              color: isDarkMode ? 'white' : '#1f2937',
            }}>
              营养摄入达成度
            </Text>
            
            {/* 今日摄入对比 */}
            <Text style={{
              fontSize: 16,
              fontWeight: 'bold',
              marginBottom: 8,
              color: isDarkMode ? '#e0e0e0' : '#4b5563',
            }}>
              今日实际摄入VS推荐摄入对比
            </Text>
            
            {/* 卡路里进度条 */}
            <View style={{
              marginBottom: 12,
              backgroundColor: isDarkMode ? '#8295b7' : '#f3f4f6',
              borderRadius: 8,
              padding: 12,
            }}>
              <View style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                marginBottom: 4,
              }}>
                <Text style={{
                  fontSize: 14,
                  color: isDarkMode ? '#e0e0e0' : '#6b7280',
                }}>
                  卡路里
                </Text>
                <Text style={{
                  fontSize: 14,
                  color: isDarkMode ? '#e0e0e0' : '#6b7280',
                }}>
                  {nutritionIntake.today.calories} / {nutritionIntake.recommended.calories} 千卡
                </Text>
              </View>
              <View style={{
                height: 8,
                backgroundColor: isDarkMode ? '#4b5563' : '#e5e7eb',
                borderRadius: 4,
                overflow: 'hidden',
              }}>
                <View style={{
                  width: `${Math.min(nutritionIntake.today.calories / nutritionIntake.recommended.calories * 100, 100)}%`,
                  height: '100%',
                  backgroundColor: '#2563eb',
                  borderRadius: 4,
                }} />
              </View>
            </View>
            
            {/* 蛋白质进度条 */}
            <View style={{
              marginBottom: 12,
              backgroundColor: isDarkMode ? '#8295b7' : '#f3f4f6',
              borderRadius: 8,
              padding: 12,
            }}>
              <View style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                marginBottom: 4,
              }}>
                <Text style={{
                  fontSize: 14,
                  color: isDarkMode ? '#e0e0e0' : '#6b7280',
                }}>
                  蛋白质
                </Text>
                <Text style={{
                  fontSize: 14,
                  color: isDarkMode ? '#e0e0e0' : '#6b7280',
                }}>
                  {nutritionIntake.today.protein} / {nutritionIntake.recommended.protein} 克
                </Text>
              </View>
              <View style={{
                height: 8,
                backgroundColor: isDarkMode ? '#4b5563' : '#e5e7eb',
                borderRadius: 4,
                overflow: 'hidden',
              }}>
                <View style={{
                  width: `${Math.min(nutritionIntake.today.protein / nutritionIntake.recommended.protein * 100, 100)}%`,
                  height: '100%',
                  backgroundColor: '#10b981',
                  borderRadius: 4,
                }} />
              </View>
            </View>
            
            {/* 碳水进度条 */}
            <View style={{
              marginBottom: 12,
              backgroundColor: isDarkMode ? '#8295b7' : '#f3f4f6',
              borderRadius: 8,
              padding: 12,
            }}>
              <View style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                marginBottom: 4,
              }}>
                <Text style={{
                  fontSize: 14,
                  color: isDarkMode ? '#e0e0e0' : '#6b7280',
                }}>
                  碳水化合物
                </Text>
                <Text style={{
                  fontSize: 14,
                  color: isDarkMode ? '#e0e0e0' : '#6b7280',
                }}>
                  {nutritionIntake.today.carbs} / {nutritionIntake.recommended.carbs} 克
                </Text>
              </View>
              <View style={{
                height: 8,
                backgroundColor: isDarkMode ? '#4b5563' : '#e5e7eb',
                borderRadius: 4,
                overflow: 'hidden',
              }}>
                <View style={{
                  width: `${Math.min(nutritionIntake.today.carbs / nutritionIntake.recommended.carbs * 100, 100)}%`,
                  height: '100%',
                  backgroundColor: '#f59e0b',
                  borderRadius: 4,
                }} />
              </View>
            </View>
            
            {/* 脂肪进度条 */}
            <View style={{
              marginBottom: 20,
              backgroundColor: isDarkMode ? '#8295b7' : '#f3f4f6',
              borderRadius: 8,
              padding: 12,
            }}>
              <View style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                marginBottom: 4,
              }}>
                <Text style={{
                  fontSize: 14,
                  color: isDarkMode ? '#e0e0e0' : '#6b7280',
                }}>
                  脂肪
                </Text>
                <Text style={{
                  fontSize: 14,
                  color: isDarkMode ? '#e0e0e0' : '#6b7280',
                }}>
                  {nutritionIntake.today.fat} / {nutritionIntake.recommended.fat} 克
                </Text>
              </View>
              <View style={{
                height: 8,
                backgroundColor: isDarkMode ? '#4b5563' : '#e5e7eb',
                borderRadius: 4,
                overflow: 'hidden',
              }}>
                <View style={{
                  width: `${Math.min(nutritionIntake.today.fat / nutritionIntake.recommended.fat * 100, 100)}%`,
                  height: '100%',
                  backgroundColor: '#ec4899',
                  borderRadius: 4,
                }} />
              </View>
            </View>
            
            {/* 目标达成率 */}
            <Text style={{
              fontSize: 16,
              fontWeight: 'bold',
              marginBottom: 8,
              color: isDarkMode ? '#e0e0e0' : '#4b5563',
            }}>
              目标达成率环形进度展示
            </Text>
            <View style={{
              marginBottom: 12,
              alignItems: 'center',
              backgroundColor: isDarkMode ? '#8295b7' : '#f3f4f6',
              borderRadius: 8,
              padding: 16,
              flexDirection: 'row',
            }}>
              <View style={{ flex: 1, alignItems: 'center' }}>
                <ProgressChart
                  data={getNutrientCompletionData()}
                  width={screenWidth * 0.6}
                  height={170}
                  strokeWidth={16}
                  radius={32}
                  chartConfig={progressChartConfig}
                  hideLegend={false}
                />
              </View>
              <View style={{ flex: 1, justifyContent: 'center' }}>
                <Text style={{
                  fontSize: 16,
                  color: isDarkMode ? '#e0e0e0' : '#6b7280',
                  marginBottom: 8,
                  textAlign: 'center',
                }}>
                  总体达成率
                </Text>
                <Text style={{
                  fontSize: 36,
                  fontWeight: 'bold',
                  color: '#2563eb',
                  textAlign: 'center',
                }}>
                  {Math.round(overallCompletion * 100)}%
                </Text>
              </View>
            </View>
          </View>
        </>
      )}
    </ScrollView>
  );
};

export default HealthDataOverviewScreen; 